import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { refreshAllCachedData } from "../services/api";
import useToast from "../hooks/useToast";
import { WalletIcon } from "@heroicons/react/24/outline";
import { TicketIcon } from "@heroicons/react/24/outline";

import {
  ChartBarIcon,
  CalendarIcon,
  DevicePhoneMobileIcon,
  DocumentTextIcon,
  UserPlusIcon,
  ArrowRightOnRectangleIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  QrCodeIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";

const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useToast();

  const handleLogout = () => {
    logout();
    showSuccess("Logged out successfully");
  };

  const handleRefreshCache = async () => {
    try {
      await refreshAllCachedData();
      showSuccess("Cache refreshed");
    } catch (err) {
      showError("Failed to refresh cache");
    }
  };

  const getMenuItems = () => {
    if (!user) return [];

    // Attendee / regular user
    if (user.role === "user") {
      return [
        {
          path: "/attendee-dashboard",
          name: "My Dashboard",
          icon: ChartBarIcon,
        },
        {
          path: "/my-wallet",
          name: "My Wallet",
          icon: WalletIcon,
        },
      ];
    }

    // Dashboard for admin-side users
    const items = [
      {
        path: "/dashboard",
        name: "Dashboard",
        icon: ChartBarIcon,
      },
    ];

    switch (user.role) {
      case "super_admin":
        items.push(
          { path: "/events-manager", name: "Events", icon: CalendarIcon },
          {
            path: "/user-management",
            name: "User Management",
            icon: UserGroupIcon,
          },
          { path: "/sessions", name: "Session Logs", icon: DocumentTextIcon },
          { path: "/register", name: "Register User", icon: UserPlusIcon },
          {
            path: "/custom-fields",
            name: "Custom Fields",
            icon: Cog6ToothIcon,
          },
          // { path: "/ticket-types", name: "Ticket Types", icon: TicketIcon },

          // ❌ REMOVED: { path: "/wallet", name: "Wallet", icon: WalletIcon },
        );
        break;

      case "admin":
        items.push(
          { path: "/events-manager", name: "Events", icon: CalendarIcon },
          { path: "/sessions", name: "Session Logs", icon: DocumentTextIcon },
          {
            path: "/user-management",
            name: "User Management",
            icon: UserGroupIcon,
          },
          // ❌ REMOVED: { path: "/wallet", name: "Wallet", icon: WalletIcon }
        );
        break;

      case "vendor":
        return [
          { path: "/vendor/overview", name: "Overview", icon: ChartBarIcon },
          { path: "/vendor/food", name: "Food Items", icon: DocumentTextIcon },
          { path: "/vendor/orders", name: "Orders", icon: DocumentTextIcon },
          {
            path: "/vendor/transactions",
            name: "Transactions",
            icon: DocumentTextIcon,
          },
          {
            path: "/vendor/meal-counter",
            name: "Meal Counter",
            icon: TicketIcon,
          },
        ];

      case "event_admin":
        items.push(
          { path: "/my-events", name: "My Events", icon: CalendarIcon },
          {
            path: "/assign-scanners",
            name: "Assign Scanners",
            icon: QrCodeIcon,
          },
          { path: "/sessions", name: "Session Logs", icon: DocumentTextIcon },
        );
        break;

      case "event_editor":
  return [
    { path: "/my-events", name: "My Events", icon: CalendarIcon },
    {
      path: "/assign-scanners",
      name: "Assign Scanners",
      icon: QrCodeIcon,
    },
  ];

      case "device_scanner":
        return [{ path: "/my-events", name: "My Events", icon: CalendarIcon }];

      default:
        break;
    }

    return items;
  };

  const menuItems = getMenuItems();

  return (
    <aside className="fixed left-0 top-[86px] w-64 h-[calc(100vh-86px)] bg-white border-r border-gray-200 flex flex-col">
      <div className="flex-1 px-4 py-6">
        <div className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
    ${
      active
        ? "bg-[#1672B9] text-white shadow-sm"
        : "text-gray-600 hover:bg-[#1672B9] hover:text-white"
    }
  `}
              >
                <Icon
                  className={`
      w-5 h-5 transition-colors duration-200
      ${active ? "text-white" : "text-[#1672B9] group-hover:text-white"}
    `}
                />
                <span className="text-sm font-medium">{item.name}</span>
              </Link>
            );
          })}
        </div>
      </div>
      <div className="p-4 border-t border-gray-100">
        <div className="mb-4">
          <p className="font-medium text-gray-800">{user?.name}</p>
          <p className="text-sm text-gray-500 capitalize">
            {user?.role?.replace("_", " ")}
          </p>
        </div>
        <button
          onClick={handleRefreshCache}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm rounded-lg bg-[#1672B9] text-white hover:bg-[#0f5a9e] transition-all duration-200 mb-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh Cache
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm rounded-lg bg-[#1672B9] text-white hover:bg-[#0f5a9e] transition-all duration-200"
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          Logout
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
